import React from 'react';
import { motion } from 'framer-motion';

interface LoadingScreenProps {
  /**
   * Distinguishes WHICH loading state is on screen.
   *
   * Every LoadingScreen renders the same markup, including the literal text
   * "Loading" — so a test asserting `getByText('Loading')` cannot tell App's
   * route-level Suspense fallback apart from a page's own loading branch. That
   * ambiguity made a real regression test vacuous: PortalStatusPanel's
   * "waits on membershipLoading" case passed against a Portal.tsx with its
   * loading branch deleted, because it was matching the Suspense fallback
   * (PR #488). Naming each mount point makes the states distinguishable.
   */
  testId?: string;
}

export function LoadingScreen({ testId = 'loading-screen' }: LoadingScreenProps = {}) {
  return (
    <div data-testid={testId} className="min-h-screen bg-earth-900 flex items-center justify-center">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="text-center"
      >
        {/* Animated Logo/Spinner */}
        <div className="relative mb-8">
          {/* Outer ring */}
          <motion.div
            className="w-24 h-24 rounded-full border-4 border-sand-400/20"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            {/* Inner spinner */}
            <motion.div
              className="absolute top-0 left-0 w-24 h-24 rounded-full border-4 border-transparent border-t-sand-400"
              animate={{ rotate: -360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>
          
          {/* Center dot that pulses */}
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-sand-400 rounded-full"
            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.7, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>

        {/* Loading text */}
        <motion.h2 
          className="text-2xl font-brand-display text-sand-100 mb-3"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Loading
        </motion.h2>
        
        {/* Animated dots */}
        <div className="flex justify-center space-x-2">
          {[0, 1, 2].map((index) => (
            <motion.div
              key={index}
              className="w-2 h-2 bg-sand-400 rounded-full"
              animate={{ 
                y: [0, -10, 0],
                opacity: [0.3, 1, 0.3]
              }}
              transition={{ 
                duration: 1,
                repeat: Infinity,
                delay: index * 0.2
              }}
            />
          ))}
        </div>

        {/* Optional loading message */}
        <motion.p 
          className="text-sand-300 text-sm mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
        >
          Preparing your experience...
        </motion.p>
      </motion.div>
    </div>
  );
} 
